import dayjs, { Dayjs } from 'dayjs';
import type { ActivityBoardPermissionItem, HolidayItem } from 'library/types';

export const REGULAR_WORKDAY_HOURS = 7.5;
export const HALF_DAY_WORK_HOURS = 4;

const DATE_KEY_FORMAT = 'YYYY-MM-DD';
const PUBLIC_HOLIDAY_DATE_FORMAT = 'DD.MM.YYYY';
const WORKDAY_START_HOUR = 9;
const WORKDAY_END_HOUR = 17;
const WORKDAY_END_MINUTE = 30;
const HALF_DAY_END_HOUR = 13;
const MIN_EFFECTIVE_HOURS = 1e-10;

type HolidayKind = 'half' | 'full';

type WorkSchedule = {
  capacity: number;
  start: Dayjs;
  end: Dayjs;
};

type WeightedPermissionDay = {
  capacity: number;
  dateKey: string;
  weight: number;
};

type PermissionDayAllocation = {
  assignedUserId: number;
  capacity: number;
  dateKey: string;
  hours: number;
  sourceIndex: number;
};

export type EffectivePermission = ActivityBoardPermissionItem & {
  effectiveHours: number;
};

const createHolidayIndex = (holidays: readonly HolidayItem[]): ReadonlyMap<string, HolidayKind> => {
  const index = new Map<string, HolidayKind>();

  holidays.forEach((holiday) => {
    const dateKey = holiday.localeDateString?.trim();
    if (!dateKey) return;

    const currentKind = index.get(dateKey);

    // A full holiday always wins if the API happens to return more than one
    // record for the same calendar day.
    if (!holiday.isHalfDay || currentKind === 'full') {
      index.set(dateKey, 'full');
    } else {
      index.set(dateKey, 'half');
    }
  });

  return index;
};

const getWorkSchedule = (date: Dayjs, holidayIndex: ReadonlyMap<string, HolidayKind>): WorkSchedule | null => {
  const dayOfWeek = date.day();
  if (dayOfWeek === 0 || dayOfWeek === 6) return null;

  const holidayKind = holidayIndex.get(date.format(PUBLIC_HOLIDAY_DATE_FORMAT));
  if (holidayKind === 'full') return null;

  const start = date.hour(WORKDAY_START_HOUR).minute(0).second(0).millisecond(0);

  if (holidayKind === 'half') {
    return {
      capacity: HALF_DAY_WORK_HOURS,
      start,
      end: date.hour(HALF_DAY_END_HOUR).minute(0).second(0).millisecond(0),
    };
  }

  return {
    capacity: REGULAR_WORKDAY_HOURS,
    start,
    end: date.hour(WORKDAY_END_HOUR).minute(WORKDAY_END_MINUTE).second(0).millisecond(0),
  };
};

const isValidInclusiveRange = (startDate: Dayjs, endDate: Dayjs): boolean =>
  startDate.isValid() && endDate.isValid() && !endDate.isBefore(startDate, 'day');

const getRangeBounds = (startDate: Dayjs, endDate: Dayjs) => ({
  start: startDate.startOf('day'),
  endExclusive: endDate.startOf('day').add(1, 'day'),
});

const getOverlapMinutes = (rangeStart: Dayjs, rangeEnd: Dayjs, windowStart: Dayjs, windowEnd: Dayjs): number => {
  const overlapStart = Math.max(rangeStart.valueOf(), windowStart.valueOf());
  const overlapEnd = Math.min(rangeEnd.valueOf(), windowEnd.valueOf());

  return overlapEnd > overlapStart ? (overlapEnd - overlapStart) / 60_000 : 0;
};

const getWeightedPermissionDays = (
  permissionStart: Dayjs,
  permissionEnd: Dayjs,
  holidayIndex: ReadonlyMap<string, HolidayKind>
): WeightedPermissionDay[] => {
  const days: WeightedPermissionDay[] = [];
  let cursor = permissionStart.startOf('day');

  // permissionEnd is deliberately exclusive. Consequently an end value at
  // exactly midnight does not add the following calendar day.
  while (cursor.isBefore(permissionEnd)) {
    const schedule = getWorkSchedule(cursor, holidayIndex);

    if (schedule) {
      const scheduleMinutes = schedule.end.diff(schedule.start, 'minute', true);
      const overlapMinutes = getOverlapMinutes(permissionStart, permissionEnd, schedule.start, schedule.end);

      if (scheduleMinutes > 0 && overlapMinutes > 0) {
        days.push({
          capacity: schedule.capacity,
          dateKey: cursor.format(DATE_KEY_FORMAT),
          weight: schedule.capacity * (overlapMinutes / scheduleMinutes),
        });
      }
    }

    cursor = cursor.add(1, 'day');
  }

  return days;
};

const createPermissionDayAllocations = (
  permission: ActivityBoardPermissionItem,
  sourceIndex: number,
  holidayIndex: ReadonlyMap<string, HolidayKind>
): PermissionDayAllocation[] => {
  if (!Number.isFinite(permission.estimatedHours) || permission.estimatedHours <= 0) return [];

  const permissionStart = dayjs(permission.startDate);
  const permissionEnd = dayjs(permission.endDate);

  if (!permissionStart.isValid() || !permissionEnd.isValid() || !permissionEnd.isAfter(permissionStart)) return [];

  const weightedDays = getWeightedPermissionDays(permissionStart, permissionEnd, holidayIndex);
  const totalWeight = weightedDays.reduce((total, day) => total + day.weight, 0);

  if (totalWeight <= 0) return [];

  const totalCapacity = weightedDays.reduce((total, day) => total + day.capacity, 0);
  const boundedTotalHours = Math.min(permission.estimatedHours, totalCapacity);
  const hoursByDay = new Array<number>(weightedDays.length).fill(0);
  const fullyCoveredDayIndexes = weightedDays
    .map((day, index) => ({ day, index }))
    .filter(({ day }) => day.weight >= day.capacity - MIN_EFFECTIVE_HOURS)
    .map(({ index }) => index);
  const fullyCoveredCapacity = fullyCoveredDayIndexes.reduce((total, index) => total + weightedDays[index].capacity, 0);
  let remainingHours = boundedTotalHours;
  let remainingDayIndexes = weightedDays.map((_, index) => index);

  // Full working days have an unambiguous value. Allocate those first when
  // the authoritative total contains enough hours, and use start/end weights
  // only to split the remainder between partial boundary days.
  if (boundedTotalHours >= fullyCoveredCapacity && fullyCoveredDayIndexes.length > 0) {
    const fullyCoveredSet = new Set(fullyCoveredDayIndexes);
    for (const index of fullyCoveredDayIndexes) {
      hoursByDay[index] = weightedDays[index].capacity;
      remainingHours -= weightedDays[index].capacity;
    }
    remainingDayIndexes = remainingDayIndexes.filter((index) => !fullyCoveredSet.has(index));
  }

  // Distribute proportionally to timestamp coverage. If a day reaches its
  // capacity, redistribute the overflow instead of silently losing hours.
  while (remainingHours > MIN_EFFECTIVE_HOURS && remainingDayIndexes.length > 0) {
    let remainingWeight = 0;
    for (const index of remainingDayIndexes) remainingWeight += weightedDays[index].weight;
    if (remainingWeight <= 0) break;

    const saturatedDayIndexes: number[] = [];
    for (const index of remainingDayIndexes) {
      const day = weightedDays[index];
      const proportionalHours = remainingHours * (day.weight / remainingWeight);
      if (proportionalHours >= day.capacity) saturatedDayIndexes.push(index);
    }

    if (saturatedDayIndexes.length === 0) {
      for (const index of remainingDayIndexes) {
        hoursByDay[index] = remainingHours * (weightedDays[index].weight / remainingWeight);
      }
      remainingHours = 0;
      break;
    }

    const saturatedSet = new Set(saturatedDayIndexes);
    for (const index of saturatedDayIndexes) {
      hoursByDay[index] = weightedDays[index].capacity;
      remainingHours -= weightedDays[index].capacity;
    }

    const nextRemainingDayIndexes: number[] = [];
    for (const index of remainingDayIndexes) {
      if (!saturatedSet.has(index)) nextRemainingDayIndexes.push(index);
    }
    remainingDayIndexes = nextRemainingDayIndexes;
  }

  return weightedDays.map((day, index) => ({
    assignedUserId: permission.assignedUserId,
    capacity: day.capacity,
    dateKey: day.dateKey,
    hours: hoursByDay[index],
    sourceIndex,
  }));
};

const capDailyUserAllocations = (allocations: PermissionDayAllocation[]): PermissionDayAllocation[] => {
  const groups = new Map<string, PermissionDayAllocation[]>();

  allocations.forEach((allocation) => {
    const groupKey = `${allocation.assignedUserId}:${allocation.dateKey}`;
    const group = groups.get(groupKey);

    if (group) {
      group.push(allocation);
    } else {
      groups.set(groupKey, [allocation]);
    }
  });

  const cappedAllocations: PermissionDayAllocation[] = [];

  groups.forEach((group) => {
    const capacity = group[0]?.capacity ?? 0;
    const totalHours = group.reduce((total, allocation) => total + allocation.hours, 0);
    const scale = totalHours > capacity && totalHours > 0 ? capacity / totalHours : 1;

    group.forEach((allocation) => {
      cappedAllocations.push({ ...allocation, hours: allocation.hours * scale });
    });
  });

  return cappedAllocations;
};

export const calculateExpectedWorkingHours = (
  startDate: Dayjs,
  endDate: Dayjs,
  holidays: readonly HolidayItem[]
): number => {
  if (!isValidInclusiveRange(startDate, endDate)) return 0;

  const holidayIndex = createHolidayIndex(holidays);
  const { start, endExclusive } = getRangeBounds(startDate, endDate);
  let totalHours = 0;
  let cursor = start;

  while (cursor.isBefore(endExclusive)) {
    totalHours += getWorkSchedule(cursor, holidayIndex)?.capacity ?? 0;
    cursor = cursor.add(1, 'day');
  }

  return totalHours;
};

/**
 * Allocates each permission's authoritative estimatedHours over its covered
 * working days and returns only the part inside the inclusive report range.
 * Source objects are never mutated. If permissions overlap for one user, their
 * combined hours are proportionally capped at that calendar day's capacity.
 */
export const calculateEffectivePermissions = (
  permissionItems: readonly ActivityBoardPermissionItem[],
  startDate: Dayjs,
  endDate: Dayjs,
  holidays: readonly HolidayItem[],
  assignedUserId: number | null = null
): EffectivePermission[] => {
  if (!isValidInclusiveRange(startDate, endDate)) return [];

  const holidayIndex = createHolidayIndex(holidays);
  const { start, endExclusive } = getRangeBounds(startDate, endDate);
  const rangeStartKey = start.format(DATE_KEY_FORMAT);
  const rangeEndExclusiveKey = endExclusive.format(DATE_KEY_FORMAT);
  const sourceItems = new Map<number, ActivityBoardPermissionItem>();
  const allocations: PermissionDayAllocation[] = [];

  permissionItems.forEach((permission, sourceIndex) => {
    if (assignedUserId !== null && permission.assignedUserId !== assignedUserId) return;

    sourceItems.set(sourceIndex, permission);
    const permissionAllocations = createPermissionDayAllocations(permission, sourceIndex, holidayIndex).filter(
      (allocation) => allocation.dateKey >= rangeStartKey && allocation.dateKey < rangeEndExclusiveKey
    );
    allocations.push(...permissionAllocations);
  });

  const hoursBySourceIndex = new Map<number, number>();

  capDailyUserAllocations(allocations).forEach((allocation) => {
    hoursBySourceIndex.set(
      allocation.sourceIndex,
      (hoursBySourceIndex.get(allocation.sourceIndex) ?? 0) + allocation.hours
    );
  });

  return Array.from(hoursBySourceIndex.entries())
    .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
    .flatMap(([sourceIndex, effectiveHours]) => {
      const source = sourceItems.get(sourceIndex);
      if (!source || effectiveHours <= MIN_EFFECTIVE_HOURS) return [];

      return [{ ...source, effectiveHours }];
    });
};

export const sumPermissionHoursByUser = (permissions: readonly EffectivePermission[]): ReadonlyMap<number, number> => {
  const hoursByUserId = new Map<number, number>();

  permissions.forEach((permission) => {
    if (!Number.isFinite(permission.effectiveHours) || permission.effectiveHours <= 0) return;

    hoursByUserId.set(
      permission.assignedUserId,
      (hoursByUserId.get(permission.assignedUserId) ?? 0) + permission.effectiveHours
    );
  });

  return hoursByUserId;
};
